import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Table } from 'primeng/table';

// PrimeNG Services
import { MessageService, ConfirmationService } from 'primeng/api';

// PrimeNG Components
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

// Services
import { ExtractsService } from '../../services/extracts-service/extracts.service';
import { DestinatairesService } from '../../services/destinataires-service/destinataires.service';

interface DoItem {
  code: string;
  nom: string;
  label: string;
  value: string;
}

interface EmailRecipients {
  to: string[];
  cc: string[];
}

interface EditFormData {
  to: string[];
  cc: string[];
  newEmail: string;
}

interface TableRow {
  type?: string;
  filtre?: string;
  to: string[];
  cc: string[];
}

@Component({
  selector: 'app-gestion-desinataires',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbModule,
    DropdownModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    CardModule,
    ChipModule,
    TagModule,
    TooltipModule,
    ProgressSpinnerModule,
    ToastModule,
    ConfirmDialogModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './gestion-desinataires.component.html',
  styleUrls: ['./gestion-desinataires.component.scss']
})
export class GestionDesinatairesComponent implements OnInit {
  @ViewChild('dt') dt!: Table;

  // Navigation
  breadcrumbHome = { icon: 'pi pi-database', routerLink: '/' };
  breadcrumbItems = [{ label: 'Gestion Destinataires' }];

  // Données
  doList: DoItem[] = [];
  filteredDoList: DoItem[] = [];

  recipients: EmailRecipients = { to: [], cc: [] };
  dataSource: TableRow[] = [];

  // État UI
  isLoading: boolean = false;
  showRecipientsSection: boolean = false;
  displayEditModal: boolean = false;

  // Mode d'affichage
  displayMode: 'table' | 'simple' = 'simple';
  tableTitle: string = '';
  columns: string[] = [];
  emailError: boolean = false;

  // Édition
  currentEditContext: any = {};
  editFormData: EditFormData = { to: [], cc: [], newEmail: '' };

  private _selectedDo: DoItem | null = null;

  constructor(
    private extractsService: ExtractsService,
    private destinatairesService: DestinatairesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadDoList();
  }

  get selectedDo(): DoItem | null {
    return this._selectedDo;
  }

  set selectedDo(value: DoItem | null) {
    if (this._selectedDo !== value) {
      this._selectedDo = value;
      if (value && value.nom) {
        this.loadRecipients(value.nom);
      } else {
        this.showRecipientsSection = false;
      }
    }
  }

  // Méthodes de filtrage
  onCodeFilter(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input && this.dt) {
      this.dt.filter(input.value, 'code', 'contains');
    }
  }

  onNomFilter(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input && this.dt) {
      this.dt.filter(input.value, 'nom', 'contains');
    }
  }

  private async loadDoList(): Promise<void> {
    try {
      const doData = await this.extractsService.list_all_do().toPromise();
      this.doList = this.mapDoData(doData);
      this.filteredDoList = [...this.doList];
    } catch (error) {
      this.handleError('Erreur lors du chargement des DO');
    } finally {
    }
  }

  private mapDoData(data: any): DoItem[] {
    if (!Array.isArray(data)) return [];

    return data.map((item: any) => {
      let code = '';
      let nom = '';

      if (Array.isArray(item) && item.length >= 2) {
        code = item[0] || 'N/A';
        nom = item[1] || 'Sans nom';
      } else if (typeof item === 'object' && item !== null) {
        code = item.code || 'N/A';
        nom = item.nom || item.name || 'Sans nom';
      } else {
        const label = item?.toString() || 'Inconnu';
        code = label;
        nom = label;
      }

      return {
        code: code,
        nom: nom,
        label: nom,
        value: nom
      };
    });
  }

  private loadRecipients(doName: string): void {
    const formattedDoName = this.formatDoName(doName);

    // console.log('Loading recipients for:', formattedDoName);

    this.destinatairesService.getDestinataires(formattedDoName).subscribe({
      next: (response) => {
        // console.log('API Response:', response);
        this.handleRecipientsResponse(response);
      },
      error: (err) => {
        // console.error('Error loading recipients:', err);
        this.handleError('DO sans rapport disponible');
      },
    });
  }

  private formatDoName(doName: string): string {
    if (!doName) return '';

    const upperDoName = doName.toUpperCase();
    return upperDoName.startsWith('MANPOWER') ? 'MANPOWER' : doName;
  }

private handleRecipientsResponse(response: any): void {
  // Vérifie si la réponse contient des destinataires
  const hasRecipients = this.checkIfHasRecipients(response);

  if (!hasRecipients) {
    this.messageService.add({
      severity: 'warn',
      summary: 'Aucun destinataire',
      detail: 'Pas de liste de destinataires pour ce DO'
    });
    this.displayMode = 'simple';
    this.recipients = { to: [], cc: [] };
    this.showRecipientsSection = true; // afficher la section même vide
    return;
  }

  this.analyzeDataStructure(response);
  this.showRecipientsSection = true;
}

private checkIfHasRecipients(data: any): boolean {
  if (!data) return false;

  if (Array.isArray(data) && data.length > 0) {
    // Tableau de rapports
    return data.some(item => (item.emails?.to?.length || 0) > 0);
  } else if (data.emails?.to) {
    return data.emails.to.length > 0;
  } else if (typeof data === 'object') {
    return Object.values(data).some((val: any) => (val.to?.length || val.emails?.to?.length || 0) > 0);
  }
  return false;
}

  private analyzeDataStructure(data: any): void {
    // console.log('Data structure received:', data);

    if (Array.isArray(data) && data.length > 0 && data[0].report_type) {
      // Cas où c'est un tableau d'objets avec report_type
      this.setupTableModeFromArray(data, 'Type de Rapport', 'type');
    } else if (data.emails?.to) {
      // Mode simple
      this.displayMode = 'simple';
      this.recipients = data.emails;
    } else if (typeof data === 'object') {
      // Mode tableau avec filtres (ancienne structure)
      this.setupTableMode(Object.entries(data), 'Filtre', 'filtre');
    } else {
      // console.error('Structure de données non reconnue:', data);
      this.displayMode = 'simple';
      this.recipients = { to: [], cc: [] };
    }
  }

  private setupTableModeFromArray(data: any[], title: string, rowTypeProperty: string): void {
    this.displayMode = 'table';
    this.tableTitle = title;
    this.columns = [title, 'To', 'CC'];

    this.dataSource = data.map(item => ({
      [rowTypeProperty]: item.report_type || item.type || 'Inconnu',
      to: item.emails?.to || [],
      cc: item.emails?.cc || []
    }));

    // console.log('DataSource created from array:', this.dataSource);
  }

  private setupTableMode(data: any[], title: string, rowTypeProperty: string): void {
    this.displayMode = 'table';
    this.tableTitle = title;
    this.columns = [title, 'To', 'CC'];

    this.dataSource = data.map(item => {
      const key = item[0];
      const value = item[1];

      return {
        [rowTypeProperty]: key,
        to: value?.to || value?.emails?.to || [],
        cc: value?.cc || value?.emails?.cc || []
      };
    });

    // console.log('DataSource created from object:', this.dataSource);
  }

  private handleError(message: string): void {
    console.error(message);
    this.messageService.add({
      severity: 'error',
      summary: 'Erreur',
      detail: message
    });
  }

  // Méthodes d'édition des emails
  openEditModal(context: any): void {
    this.currentEditContext = context;

    const sourceData = this.displayMode === 'simple' ? this.recipients : context.rowData;

    this.editFormData = {
      to: [...(sourceData.to || [])],
      cc: [...(sourceData.cc || [])],
      newEmail: ''
    };

    this.displayEditModal = true;
  }

  addEmail(): void {
    const email = this.editFormData.newEmail.trim();
  if (!email) {
        this.emailError = true;
        return;
      }

      // Validation basique d'email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.emailError = true;
        return;
      }

      this.emailError = false;
      if (!this.isValidEmail(email)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Veuillez entrer une adresse email valide'
        });
        return;
      }

    if (this.editFormData.to.includes(email) || this.editFormData.cc.includes(email)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attention',
        detail: 'Cet email existe déjà dans la liste'
      });
      return;
    }

    this.editFormData.to.push(email);
    this.editFormData.newEmail = '';
  }

  removeEmail(email: string, listType: 'to' | 'cc'): void {
    this.editFormData[listType] = this.editFormData[listType].filter(e => e !== email);
  }

  // Méthodes pour déplacer les emails entre les listes
  moveToCC(email: string): void {
    this.removeEmail(email, 'to');
    if (!this.editFormData.cc.includes(email)) {
      this.editFormData.cc.push(email);
    }
  }

  moveToTO(email: string): void {
    this.removeEmail(email, 'cc');
    if (!this.editFormData.to.includes(email)) {
      this.editFormData.to.push(email);
    }
  }

  isValidEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

 saveChanges(): void {
  if (!this.selectedDo?.nom) {
    this.messageService.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Aucun DO sélectionné'
    });
    return;
  }

  if (this.editFormData.to.length === 0) {
    this.messageService.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'La liste "To" ne peut pas être vide. Ajoutez au moins une adresse email.'
    });
    return;
  }

  const formattedDoName = this.formatDoName(this.selectedDo.nom);
  let updateContext = {};

  if (this.displayMode === 'table' && this.currentEditContext.rowData) {
    const rowData = this.currentEditContext.rowData;
    updateContext = rowData.filtre ? { filtre: rowData.filtre } : { report_type: rowData.type };
  }

  this.destinatairesService.updateDestinataires(
    formattedDoName,
    this.editFormData.to,
    this.editFormData.cc,
    updateContext
  ).subscribe({
    next: () => {
      this.messageService.add({
        severity: 'success',
        summary: 'Succès',
        detail: 'Modifications enregistrées'
      });
      this.displayEditModal = false;
      this.loadRecipients(formattedDoName);
    },
    error: (err) => this.handleError('Échec de la mise à jour')
  });
}


  hasChanges(): boolean {
    if (this.displayMode === 'simple') {
      return !this.arraysEqual(this.editFormData.to, this.recipients.to) ||
             !this.arraysEqual(this.editFormData.cc, this.recipients.cc);
    } else {
      const rowData = this.currentEditContext.rowData;
      return !this.arraysEqual(this.editFormData.to, rowData.to || []) ||
             !this.arraysEqual(this.editFormData.cc, rowData.cc || []);
    }
  }

  private arraysEqual(a: any[], b: any[]): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  }

  // Méthodes utilitaires pour le template
  getSelectedDoName(): string {
    return this.selectedDo?.nom || '';
  }

  selectDo(nom: string): void {
    const doItem = this.doList.find(item => item.nom === nom);
    this.selectedDo = doItem || null;
  }

  editDestinataires(nom: string): void {
    this.selectDo(nom);
  }

  closeRecipients(): void {
    this.showRecipientsSection = false;
    this.selectedDo = null;
  }

  // Méthode pour filtrer le tableau
  filterTable(event: Event, field: string): void {
    const value = (event.target as HTMLInputElement).value;
    if (this.dt) {
      this.dt.filter(value, field, 'contains');
    }
  }
}
